import { ReactNode, useState, useEffect } from "react";
import {
    ApolloClient,
    InMemoryCache,
    ApolloProvider,
    ApolloLink,
} from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { setContext } from '@apollo/client/link/context';
import { GraphQLErrors, NetworkError } from "@apollo/client/errors";
import { createUploadLink } from 'apollo-upload-client';
import { useConfig } from "../hooks";
import { useAuthenticatedUser } from "./AuthenticatedUserProvider";
import { LocalStorageWrapper, persistCache } from "apollo3-cache-persist";
import { BlankLoadingPage } from "../views/BlankLoadingPage/BlankLoadingPage";
import { AuthTokens } from '@shopify-multiseller/graphql/partner/types';

interface Props {
    children?: ReactNode;
    local?: boolean;
}

const ApolloAuthorizedProvider = ({ children, local }: Props) => {
    const [client, setClient] = useState<ApolloClient<any> | null>(null);
    const authenticatedUser = useAuthenticatedUser();
    const config = useConfig();

    useEffect(() => {
        const initializeApolloClient = async () => {
            const cache = new InMemoryCache({
                typePolicies: {
                    Query: {
                        fields: {
                            myField: {
                                merge(existing, incoming) {
                                    return incoming;
                                },
                            },
                        },
                    },
                },
            });

            await persistCache({
                cache,
                storage: new LocalStorageWrapper(window.sessionStorage),
            });

            const httpLink = createUploadLink({
                uri: local ? `http://${window.location.hostname}:8080/api/partner/graphql` : import.meta.env.VITE_APP_PARTNER_API_ENDPOINT+"",
                fetchOptions: {
                    mode: "cors",
                }
            }) as unknown as ApolloLink;

            const authLink = setContext((_, { headers }) => {
                // Read token from sessionStorage to avoid stale closure issues
                const tokensJson = sessionStorage.getItem('tokens');
                const tokens = tokensJson ? JSON.parse(tokensJson) as AuthTokens : null;
                const token = tokens?.accessToken;
                
                // Only the shop is named here. The API resolves that shop's Cognito pool from
                // its own records — sending pool ids from the client would let a caller choose
                // which pool their token is verified against (MPPRO-81).
                return {
                    headers: {
                        ...headers,
                        authorization: token ? `Bearer ${token}` : '',
                        shopid: config?.shopConfig?.auth?.shopId || '',
                    },
                };
            });

            const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
                const authError = isAuthenticationError(graphQLErrors, networkError);
                if (!authError) return;

                // refreshAccessToken is deliberately absent. It was listed here while the API
                // failed the whole request for an expired access token, which made the refresh
                // itself answer UNAUTHENTICATED and logged the seller out for asking. The API
                // now accepts an expired token for this one mutation (MPPRO-89), so an auth
                // error from a refresh means the refresh token is dead too — the one case where
                // signing out is the right answer, and the only signal the app has for it.
                const skipAuthHandling = [
                    'seller',
                    'login',
                    'loginSession'
                ].some(op => operation.operationName?.toLowerCase().includes(op.toLowerCase()));

                if (!skipAuthHandling) {
                    console.log("Authentication error, logging out");
                    authenticatedUser.destroy();
                } else {
                    console.log(`Ignoring authentication error for operation: ${operation.operationName}`);
                }
            });

            const isAuthenticationError = (graphQLErrors: GraphQLErrors | undefined, networkError: NetworkError | any | undefined): boolean => {
                const hasAuthGraphQLError = graphQLErrors?.some(err => 
                    err.extensions?.code === 'UNAUTHENTICATED' || 
                    err.message.toLowerCase().includes('unauthorized')
                );
                
                const hasAuthNetworkError = networkError && 
                    (networkError.statusCode === 401 || networkError.statusCode === 403);

                return hasAuthGraphQLError || hasAuthNetworkError;
            };

            const client = new ApolloClient({
                link: ApolloLink.from([errorLink, authLink, httpLink]),
                cache: cache,
            });

            setClient(client);
        };

        initializeApolloClient();
    }, [authenticatedUser, config, local]);

    if (!client) {
        return <BlankLoadingPage />;
    }

    return (
        <ApolloProvider client={client}>
            {children}
        </ApolloProvider>
    );
};

export { ApolloAuthorizedProvider };
